import React, { useMemo, useEffect, useRef } from "react";
import styled from "styled-components";
import { SwitchField } from "../SwitchField";
import { FIELDS } from "constants/PostFormFields";
import { VIDEO_REEL_CONSTANTS } from "constants/VideoReelConstants";
import { PostMediaFile } from "shared/types/post";
import { Icon } from "@dialogueconsulting/sked-ui";
import { PlatformIcon } from "@/entities/accounts";
import {
  getVideoReelCriteria,
  shouldShowReelToggle,
  getDefaultVideoPostType,
  isReelToggleEnabled,
} from "../../utils/videoReelUtils";
import { AccountPlatformType } from "@/shared/types/accounts";
import TooltipWrapper from "ui/TooltipWrapper/TooltipWrapper";
import { isNewPostId } from "../../../../../utils/posts";

const RawSwitchField = SwitchField as any;

interface VideoReelToggleProps {
  mediaFiles: PostMediaFile[];
  platforms?: unknown[];
  selectedPlatform: AccountPlatformType | "LINKED";
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formAccounts: any[];
  updateField: (field: string, value: string) => void;
  disabled: boolean;
  currentValue?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  platformPost?: any;
}

/**
 * Derives the saved video/reel choice from the persisted Facebook reel fields
 * when the form has not set an explicit `videoPostType` yet.
 *
 * @param platformPost Persisted post data that may include `videoPostType`,
 * `isFbReel`, or `type`.
 * @returns The persisted `"reel"` or `"video"` selection when saved post data
 * already defines it, otherwise `undefined` so the form can infer the value
 * from media criteria.
 */
const getPersistedVideoPostType = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  platformPost: any
): string | undefined => {
  if (platformPost?.videoPostType) {
    return platformPost.videoPostType;
  }

  if (platformPost?.isFbReel === true || platformPost?.type === "fb-reel") {
    return VIDEO_REEL_CONSTANTS.POST_TYPES.REEL;
  }

  if (platformPost?.isFbReel === false) {
    return VIDEO_REEL_CONSTANTS.POST_TYPES.VIDEO;
  }

  return undefined;
};

const VideoReelToggleContainer = styled.div`
  display: flex;
  margin-bottom: 1rem;
  align-items: center;
  gap: 8px;
`;

const StyledPlatformIcon = styled(PlatformIcon)`
  width: 14px;
  height: 14px;
  margin-left: 5px;
`;

// Helper function to determine initial reel state based on post type and properties
const getInitialReelState = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  platformPost: any,
  currentValue: string | undefined,
  defaultVideoPostType: string
): boolean => {
  // Check if this is a new post
  const isNewPost = isNewPostId(platformPost?._id);

  if (isNewPost) {
    // For new posts: use currentValue if it exists, otherwise use criteria-based default
    return currentValue === undefined
      ? defaultVideoPostType === VIDEO_REEL_CONSTANTS.POST_TYPES.REEL
      : currentValue === VIDEO_REEL_CONSTANTS.POST_TYPES.REEL;
  } else {
    // For existing posts: use currentValue if available, otherwise use database values
    if (currentValue !== undefined) {
      return currentValue === VIDEO_REEL_CONSTANTS.POST_TYPES.REEL;
    }

    // Check isFbReel property from database
    if (platformPost?.isFbReel !== undefined) {
      return platformPost.isFbReel === true;
    }

    // Fallback to videoPostType for backward compatibility
    if (platformPost?.videoPostType !== undefined) {
      return (
        platformPost.videoPostType === VIDEO_REEL_CONSTANTS.POST_TYPES.REEL
      );
    }

    return false;
  }
};

export const VideoReelToggle: React.FC<VideoReelToggleProps> = ({
  mediaFiles,
  selectedPlatform,
  formAccounts,
  updateField,
  disabled,
  currentValue,
  platformPost,
}) => {
  // Track whether user has manually interacted with the toggle
  const userHasInteracted = useRef(false);
  const effectiveCurrentValue =
    currentValue ?? getPersistedVideoPostType(platformPost);

  const normalizedMediaFiles = useMemo(() => {
    if (Array.isArray(mediaFiles) && mediaFiles.length > 0) {
      return mediaFiles;
    }

    if (platformPost?.isVideo && platformPost?.url) {
      return [
        {
          isVideo: platformPost.isVideo,
          url: platformPost.url,
          meta: platformPost.meta || {},
          thumbnailUrl: platformPost.thumbnailUrl || "",
          originalUrl: platformPost.originalUrl || platformPost.origUrl,
          type: platformPost.type || "video",
          fileName: platformPost.fileName,
          altText: platformPost.altText,
          description: platformPost.description,
        } as PostMediaFile,
      ];
    }

    return mediaFiles;
  }, [mediaFiles, platformPost]);

  const displayPlatforms = useMemo(() => {
    const accounts = Object.values(formAccounts || {});
    const supportedAccounts = accounts.filter((account) => {
      const platformType =
        account.platformType || account.platformName?.toUpperCase();
      return VIDEO_REEL_CONSTANTS.SUPPORTED_PLATFORMS.includes(platformType);
    });

    // Deduplicate by platform type to show only one icon per platform
    const uniquePlatformTypes = new Set();
    const uniquePlatforms = [];

    for (const account of supportedAccounts) {
      const platformType =
        account.platformType || account.platformName?.toUpperCase();
      if (!uniquePlatformTypes.has(platformType)) {
        uniquePlatformTypes.add(platformType);
        uniquePlatforms.push({
          platformType,
          platformName: account.platformName || platformType.toLowerCase(),
        });
      }
    }

    return uniquePlatforms;
  }, [formAccounts]);

  const criteria = useMemo(() => {
    const accounts = Object.values(formAccounts || {});
    const result = getVideoReelCriteria(
      normalizedMediaFiles,
      accounts,
      selectedPlatform === "LINKED" ? undefined : selectedPlatform,
      effectiveCurrentValue
    );
    return result;
  }, [
    normalizedMediaFiles,
    formAccounts,
    selectedPlatform,
    effectiveCurrentValue,
  ]);

  const showToggle = shouldShowReelToggle(criteria);
  const enableToggle = isReelToggleEnabled(criteria);
  const defaultVideoPostType = getDefaultVideoPostType(criteria);

  const isReelSelected = getInitialReelState(
    platformPost,
    effectiveCurrentValue,
    defaultVideoPostType
  );

  useEffect(() => {
    const isNewPost = isNewPostId(platformPost?._id);

    // For new posts: auto-set to reel ONLY if user hasn't manually interacted and criteria are met
    if (
      isNewPost &&
      showToggle &&
      !userHasInteracted.current &&
      defaultVideoPostType === "reel" &&
      (effectiveCurrentValue === undefined || effectiveCurrentValue === "video")
    ) {
      updateField(FIELDS.VIDEO_POST_TYPE, "reel");
    }
  }, [
    showToggle,
    effectiveCurrentValue,
    defaultVideoPostType,
    updateField,
    platformPost?._id,
  ]);

  // Auto-reset logic: when reel criteria become invalid, reset to video
  useEffect(() => {
    // Auto-reset when showToggle is false but currentValue is "reel"
    // This means the reel criteria are no longer met but the value is still "reel"
    if (!showToggle && effectiveCurrentValue === "reel") {
      updateField(FIELDS.VIDEO_POST_TYPE, "video");
    }
  }, [showToggle, effectiveCurrentValue, updateField]);

  const handleToggleChange = () => {
    // Mark that user has manually interacted with the toggle
    userHasInteracted.current = true;

    const newValue = isReelSelected
      ? VIDEO_REEL_CONSTANTS.POST_TYPES.VIDEO
      : VIDEO_REEL_CONSTANTS.POST_TYPES.REEL;

    updateField(FIELDS.VIDEO_POST_TYPE, newValue);
  };

  const getExplanationText = () => {
    if (
      !enableToggle &&
      criteria.hasVideo &&
      criteria.isNineToSixteen &&
      !criteria.isShortDuration
    ) {
      return "Video is longer than 90 seconds, it will be posted as a Video (not available as Reel).";
    }
    if (enableToggle) {
      return "Video is under 90 seconds - choose to post as Video or Reel.";
    }
    return "";
  };

  if (!showToggle) {
    return null;
  }

  const explanationText = getExplanationText();

  return (
    <VideoReelToggleContainer>
      <RawSwitchField
        label="Post as Reel"
        labelIsAfter={true}
        checked={isReelSelected}
        onChange={handleToggleChange}
        disabled={disabled || !enableToggle}
      />
      {selectedPlatform === "LINKED" &&
        displayPlatforms.map((platform) => (
          <StyledPlatformIcon
            key={platform.platformType || platform.platformName}
            type={
              platform.platformName?.toLowerCase() ||
              platform.platformType?.toLowerCase()
            }
          />
        ))}
      <TooltipWrapper tooltip={explanationText || undefined} placement="top">
        <Icon name="info-circle" color="#808080" size="sm" />
      </TooltipWrapper>
    </VideoReelToggleContainer>
  );
};
